import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Check, X } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  Select,
  Textarea,
} from '../../../components/ui';
import { getList } from '../cache';
import { amendItem, enqueue } from '../outbox';
import { refreshOfflineState, useOfflineState } from '../store';
import { syncNow } from '../sync';
import { getUserId } from '../transport';
import type { OutboxItem } from '../types';

type Kind = 'stakeholder' | 'organization';

interface Choice {
  key: string;
  label: string;
}

/** Used only when the phone has never been online long enough to cache them. */
const FALLBACK_ORG_TYPES: Choice[] = [
  { key: 'government', label: 'Government' },
  { key: 'ngo', label: 'NGO' },
  { key: 'company', label: 'Company' },
  { key: 'community', label: 'Community' },
  { key: 'other', label: 'Other' },
];

const FALLBACK_CATEGORIES: Choice[] = [
  { key: 'government-official', label: 'Government official' },
  { key: 'community-authority', label: 'Community authority' },
  { key: 'civil-society-actor', label: 'Civil society actor' },
  { key: 'business-leader', label: 'Business leader' },
];

/** The category list the app last saw, so capture works with no connection. */
async function loadChoices(kind: Kind): Promise<Choice[]> {
  const domain = kind === 'stakeholder' ? 'stakeholder_category' : 'organization_type';
  try {
    const cached = await getList('app', `/api/taxonomies?domain=${domain}`);
    const payload: any = cached?.data;
    const rows: any[] = Array.isArray(payload) ? payload : payload?.data;
    const choices = (rows ?? [])
      .filter((row) => row?.key)
      .map((row) => ({ key: String(row.key), label: String(row.label ?? row.key) }));
    if (choices.length) return choices;
  } catch {
    /* fall through to the built-in list */
  }
  return kind === 'stakeholder' ? FALLBACK_CATEGORIES : FALLBACK_ORG_TYPES;
}

/**
 * What a field officer fills in at the meeting: a name, whatever contact
 * details they were given, and a photo of the business card. It goes into the
 * queue — never into a "saved" message that is not true yet.
 */
export const CaptureSheet: React.FC<{
  kind: Kind | null;
  /** when set, the sheet fixes a queued write the server refused */
  item?: OutboxItem | null;
  onClose: () => void;
}> = ({ kind, item, onClose }) => {
  const { online } = useOfflineState();
  const [values, setValues] = useState<Record<string, string>>({});
  const [card, setCard] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [choices, setChoices] = useState<Choice[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const fixing = Boolean(item);

  useEffect(() => {
    if (!kind) return;
    setCard(null);
    setError(null);
    setValues(
      item?.body
        ? Object.fromEntries(
            Object.entries(item.body).map(([key, value]) => [key, value == null ? '' : String(value)]),
          )
        : {},
    );
    void loadChoices(kind).then(setChoices);
  }, [kind, item]);

  const preview = useMemo(() => (card ? URL.createObjectURL(card) : null), [card]);
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  const set = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setValues((current) => ({ ...current, [field]: event.target.value }));

  const save = async () => {
    if (!kind) return;
    const trimmed = Object.fromEntries(
      Object.entries(values)
        .map(([key, value]) => [key, value.trim()])
        .filter(([, value]) => value !== ''),
    ) as Record<string, string>;

    if (kind === 'stakeholder' && (!trimmed.firstName || !trimmed.lastName)) {
      setError('A first and last name are the minimum.');
      return;
    }
    if (kind === 'stakeholder' && !trimmed.category) {
      // the server insists on one; better to say so now than after syncing
      setError('Pick a category.');
      return;
    }
    if (kind === 'organization' && !trimmed.name) {
      setError('The organisation needs a name.');
      return;
    }

    setSaving(true);
    try {
      const userId = (await getUserId()) ?? undefined;
      const label =
        kind === 'stakeholder'
          ? `${trimmed.firstName} ${trimmed.lastName}`
          : trimmed.name;

      if (item) {
        // a refused write, corrected: it goes back in the queue with a fresh
        // key so the server does not replay its own refusal
        await amendItem(item.id, trimmed);
        await refreshOfflineState();
        if (online) void syncNow();
        onClose();
        return;
      }

      const created = await enqueue({
        kind: kind === 'stakeholder' ? 'stakeholder.create' : 'organization.create',
        label,
        detail: kind === 'stakeholder' ? trimmed.location : trimmed.type || 'other',
        method: 'POST',
        path: kind === 'stakeholder' ? '/api/stakeholders' : '/api/organizations',
        body: kind === 'organization' ? { type: 'other', ...trimmed } : trimmed,
        userId,
      });

      if (card) {
        await enqueue({
          kind: 'businessCard.upload',
          label,
          detail: `Card photo · ${card.name}`,
          method: 'POST',
          path: '/api/media/upload',
          file: { blob: card, name: card.name || 'business-card.jpg' },
          // the id only exists once the record above has synced
          refs: {
            query: {
              [kind === 'stakeholder' ? 'stakeholderId' : 'organizationId']: created.id,
            },
          },
          dependsOn: created.id,
          userId,
        });
      }

      await refreshOfflineState();
      if (online) void syncNow();
      onClose();
    } catch (saveError: any) {
      setError(saveError?.message ?? 'Could not queue this capture.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={kind !== null}
      onClose={onClose}
      label={kind === 'organization' ? 'Capture organisation' : 'Capture person'}
      handle
      className="h-[92dvh] sm:h-auto sm:max-h-[88vh] sm:max-w-lg"
    >
      <DialogHeader
        title={
          fixing
            ? 'Fix queued capture'
            : kind === 'organization'
              ? 'Capture organisation'
              : 'Capture person'
        }
        eyebrow={
          fixing
            ? (item?.error?.message ?? 'The server refused this')
            : online
              ? 'Sends straight away'
              : 'Queued until you are back online'
        }
        onClose={onClose}
      />
      <DialogBody className="space-y-3">
        {error && (
          <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{error}</p>
        )}

        {kind === 'organization' ? (
          <>
            <Field label="Name" htmlFor="cap-name" required>
              <Input id="cap-name" value={values.name ?? ''} onChange={set('name')} autoFocus />
            </Field>
            <Field label="Type" htmlFor="cap-type">
              <Select id="cap-type" value={values.type ?? ''} onChange={set('type')}>
                <option value="">Choose…</option>
                {choices.map((choice) => (
                  <option key={choice.key} value={choice.key}>
                    {choice.label}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Phone" htmlFor="cap-phone">
                <Input id="cap-phone" inputMode="tel" value={values.phone ?? ''} onChange={set('phone')} />
              </Field>
              <Field label="Email" htmlFor="cap-email">
                <Input id="cap-email" inputMode="email" value={values.email ?? ''} onChange={set('email')} />
              </Field>
            </div>
            <Field label="Address" htmlFor="cap-address">
              <Input id="cap-address" value={values.address ?? ''} onChange={set('address')} />
            </Field>
            <Field label="Notes" htmlFor="cap-notes">
              <Textarea id="cap-notes" value={values.description ?? ''} onChange={set('description')} />
            </Field>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="First name" htmlFor="cap-first" required>
                <Input id="cap-first" value={values.firstName ?? ''} onChange={set('firstName')} autoFocus />
              </Field>
              <Field label="Last name" htmlFor="cap-last" required>
                <Input id="cap-last" value={values.lastName ?? ''} onChange={set('lastName')} />
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {/* the entity keeps numbers in professionalPhone; plain `phone` is dropped */}
              <Field label="Phone" htmlFor="cap-phone">
                <Input
                  id="cap-phone"
                  inputMode="tel"
                  value={values.professionalPhone ?? ''}
                  onChange={set('professionalPhone')}
                />
              </Field>
              <Field label="Email" htmlFor="cap-email">
                <Input
                  id="cap-email"
                  inputMode="email"
                  value={values.professionalEmail ?? ''}
                  onChange={set('professionalEmail')}
                />
              </Field>
            </div>
            <Field label="Category" htmlFor="cap-category" required>
              <Select id="cap-category" value={values.category ?? ''} onChange={set('category')}>
                <option value="">Choose…</option>
                {choices.map((choice) => (
                  <option key={choice.key} value={choice.key}>
                    {choice.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Where you met" htmlFor="cap-location">
              <Input id="cap-location" value={values.location ?? ''} onChange={set('location')} />
            </Field>
            <Field label="Notes" htmlFor="cap-bio">
              <Textarea id="cap-bio" value={values.bio ?? ''} onChange={set('bio')} />
            </Field>
          </>
        )}

        {!fixing && (
        <Field label="Business card">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            onChange={(event) => setCard(event.target.files?.[0] ?? null)}
          />
          {card && preview ? (
            <div className="flex items-center gap-3 rounded-xl border border-neutral-200 p-2">
              <img
                src={preview}
                alt="Business card"
                className="h-14 w-20 flex-shrink-0 rounded-lg object-cover"
              />
              <span className="min-w-0 flex-1 truncate text-[12px] text-neutral-500">{card.name}</span>
              <Button size="xs" variant="ghost" onClick={() => setCard(null)}>
                <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                Remove
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="secondary" onClick={() => fileRef.current?.click()}>
              <Camera className="h-4 w-4" strokeWidth={1.75} />
              Photograph card
            </Button>
          )}
        </Field>
        )}

        <p className="text-[12px] text-neutral-400">
          {fixing
            ? 'Goes back to the front of the queue once you have corrected it.'
            : online
              ? 'Sent as soon as you save.'
              : 'Kept on this phone and sent when there is a connection. It stays in the queue until the server confirms it.'}
        </p>
      </DialogBody>
      <DialogFooter>
        <Button size="sm" variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" onClick={save} disabled={saving}>
          <Check className="h-4 w-4" strokeWidth={2} />
          {fixing ? 'Queue again' : online ? 'Save' : 'Add to queue'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
